import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { Box, Checkbox, Grid, TextField, Typography } from '@material-ui/core';
import * as _ from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { DialogForm } from '../../components/DialogForm';
import {
  ADD_CUSTOMER_PROVIDER_RELATION,
  FETCH_AUTHENTICATION_QUERY,
  WORKFLOW_SUBSCRIBERS_SUMMARY_QUERY,
  WORKFLOW_LIST,
  PARTICIPANT_QUERY,
  SUBSCRIBED_PARTICIPANT_WORKFLOW_QUERY,
  DISABLE_CUSTOMER_PROVIDER_RELATIONSHIP,
  FETCH_AUTHENTICATION_CONTEXT_QUERY
} from '../graphQL/queries';
import { requestRelationConfig } from './formDataConfig';
import { makeStyles } from '@material-ui/core/styles';
import getField, { getParameterLabel } from './utils';
import { Loading } from '../../Loading';

const useStyles = makeStyles(theme => ({
  textField: {
    width: '90%'
  },
  grid: {
    width: '100%',
    borderBottom: '1px solid #EBEBEB'
  },
  headers: {
    color: theme.palette.text.secondary
  }
}));

interface ManageRelationsProps {
  dialogOpen: true | false;
  handleDialogClose: any;
  handleDialogSubmit: any;
  participant: any;
  edit: any;
  formData: any;
  customerProviderRefresh: any;
  workflowCode: string;
  participantSubscriberRefresh: any;
  aggregatorSubscriberRefresh: any;
  existingDisable: true | false;
  supportUser: true | false;
  participantCode: any;
  userRole: any;
  selectedKey?: any;
}

type workflowInfo = {
  workflowCode: string;
  workflowName: string;
  operations?: any;
};

type workflowObj = {
  workflow: workflowContentData;
};
type workflowContentData = {
  content: workflowInfo[];
};

const ManageRelationDialog = (props: ManageRelationsProps) => {
  const { selectedKey } = props;
  let { participantCode, participantType } = props.participant;
  let { additionalCreds, partnerCode, authContext } = props.formData;

  // let dataConfig: any = [...requestRelationConfig];

  const [dataConfig, setDataConfig] = React.useState<any>([...requestRelationConfig]);

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [disableCustomerProvider] = useMutation(DISABLE_CUSTOMER_PROVIDER_RELATIONSHIP);
  const [addCustomerProvider] = useMutation(ADD_CUSTOMER_PROVIDER_RELATION);
  const workflowList = useQuery<workflowObj, {}>(WORKFLOW_LIST, {});
  const [queryOperations, setQueryOperations] = React.useState<any>({});

  const [formData, setFormData] = React.useState<any>({});
  const [operations, setOperations] = React.useState<any>({});
  const getProviderCode = (code: any) => {
    if (participantType === 'PROVIDER') return participantCode;
    else return code;
  };
  const getOperationParticipantType = (participantType: any) => {
    if (participantType === 'PROVIDER') return 'CUSTOMER';
    else return 'PROVIDER';
  };

  const getCustomerCode = (code: any) => {
    if (participantType === 'CUSTOMER') return participantCode;
    else return code;
  };

  const handleStatusToggleChange = () => {
    props.handleDialogClose();
    disableCustomerProvider({
      variables: {
        workflowCode: props.workflowCode,
        aggregatorCode: props.formData.providerCode === 'DEFAULT' ? props.formData.aggregatorCode : 'DEFAULT',
        customerCode: getCustomerCode(formData.partnerCode),
        providerCode:
          props.formData.providerCode !== 'DEFAULT' ? getProviderCode(props.formData.partnerCode) : 'DEFAULT'
      }
    })
      .then(() => {
        enqueueSnackbar('Relationship disabled successfully !!', { variant: 'success', preventDuplicate: true });
        props.customerProviderRefresh();
        props.participantSubscriberRefresh();
        if (props.workflowCode === 'freightTracking') {
          props.aggregatorSubscriberRefresh();
        }
      })
      .catch(() => {
        enqueueSnackbar('Unable to disable relationship !!', { variant: 'error', preventDuplicate: true });
      });
  };

  const [getWorkflowSummary] = useLazyQuery(WORKFLOW_SUBSCRIBERS_SUMMARY_QUERY, {
    variables: { participantType: participantType, participantCode: participantCode },
    fetchPolicy: 'network-only'
  });

  let providerCode = getProviderCode(partnerCode);
  const [getParticipantData, { loading: partnerIdentifiersLoading, data: partnerIdentifiersData }] = useLazyQuery(
    PARTICIPANT_QUERY,
    {
      variables: { participantCode: getProviderCode(partnerCode) },
      fetchPolicy: 'network-only'
    }
  );

  useEffect(() => {
    if (providerCode && providerCode !== undefined) {
      getParticipantData();
    }
  }, [providerCode]);

  const { loading: workflowSubscribersDataLoading, data: workflowSubscribersData } = useQuery(
    SUBSCRIBED_PARTICIPANT_WORKFLOW_QUERY,
    {
      variables: { participantCode: participantCode },
      fetchPolicy: 'cache-first',
      skip: !participantCode
    }
  );

  let { data: authenticationParticipantData, loading: authenticationParticipantLoading } = useQuery(
    FETCH_AUTHENTICATION_QUERY,
    {
      variables: { participantCode: getProviderCode(partnerCode) },
      skip:
        props.userRole === 'PARTICIPANT_SUPPORT' || props.userRole === 'PARTICIPANT_ADMIN' ? true : !props.dialogOpen
    }
  );
  let { data: authenticationAggregatorData } = useQuery(FETCH_AUTHENTICATION_CONTEXT_QUERY, {
    variables: {
      participantCode: authContext?.aggregatorCode,
      authContext: authContext?.authContext
    },
    skip: props.userRole === 'PARTICIPANT_SUPPORT' || props.userRole === 'PARTICIPANT_ADMIN' ? true : !props.dialogOpen
  });

  useEffect(() => {
    const operationList: any = {};
    const serviceNameFormObj = _.filter(
      workflowList.data?.workflow?.content || [],
      (o: any) => o.workflowCode === props.workflowCode
    );
    (serviceNameFormObj?.[0]?.operations || []).forEach((operation: any) => {
      if (operation.initiator === getOperationParticipantType(participantType)) {
        operationList[operation.operationCode] = {
          url: props.formData?.operationUrls?.[operation.operationCode]?.url,
          noOps: props.formData?.operationUrls?.[operation.operationCode]?.noOps || false,
          operationName: operation.operationName
        };
      }
    });
    setOperations({ ...operationList });
  }, [participantType, props.workflowCode, workflowList.data, partnerCode, participantCode, props.formData]);
  useEffect(() => {
    let formDataList: any = {};
    if (!authenticationParticipantLoading && !partnerIdentifiersLoading && props.workflowCode !== 'freightTracking') {
      let authenticationData;
      if (authContext?.aggregatorCode) {
        authenticationData = getAuthentication(authenticationAggregatorData);
        formDataList.authContext = authContext?.authContext;
      } else {
        authenticationData = getAuthentication(authenticationParticipantData);
        formDataList.authContext = authenticationData?.authContext;
      }
      const editData: any[] = [];
      const authData: any[] = [];
      const defaultDataConfig: any[] = [...requestRelationConfig];
      const field = getField('authContext', 'Auth Context');
      authData.push(field);
      if (authenticationData.additionalCreds) {
        const additionalCredsObj = authenticationData.additionalCreds;
        _.keys(additionalCredsObj || []).forEach(keyParameter => {
          const additionalParameterObj = additionalCredsObj[keyParameter];
          _.keys(additionalParameterObj || []).forEach(key => {
            const keyName = `provider_${keyParameter}_${key}`;
            const field = getField(keyName, `${getParameterLabel(keyParameter)}-${key}`);
            authData.push(field);
            formDataList[keyName] =
              props.formData?.additionalCreds?.[keyParameter]?.[key] || additionalParameterObj[key];
            if (key === 'secret') {
              formDataList[keyName] = decodeURI(formDataList[keyName]);
            }
          });
        });
      }
      let combinedCreds = partnerIdentifiersData?.participant?.content[0]?.additionalCreds;
      if (combinedCreds) {
        _.keys(combinedCreds || []).forEach(keyParameter => {
          const additionalParameterObj = combinedCreds[keyParameter];
          _.keys(additionalParameterObj || []).forEach(key => {
            const keyName = `${keyParameter}/${key}`;
            const editField = _.filter(editData || [], o => o.key === keyName);
            if (editField && editField.length > 0) {
              editField[0].disabled = false;
            } else {
              const field = getField(keyName, `${getParameterLabel(keyParameter)}-${key}`);
              field.disabled = false;
              editData.push(field);
              formDataList[keyName] = props.edit[partnerCode]
                ? _.get(additionalCreds, keyParameter + '.' + key) || ''
                : '';
              if (key === 'secret') {
                formDataList[keyName] = decodeURI(formDataList[keyName]);
              }
            }
          });
        });
      }
      if (authData.length > 0) {
        defaultDataConfig.push({
          key: 'authentication',
          label: `${authContext?.aggregatorCode ? 'Aggregator' : 'Default'} Authentication`,
          type: 'sectionHeader',
          size: 12
        });
      }
      let config = [];
      if (editData.length > 0) {
        let customerHeader = {};
        customerHeader = {
          key: 'manageAuthentication',
          label: 'Customer specific Authentication',
          type: 'sectionHeader',
          size: 12
        };
        config = [...defaultDataConfig, ...authData, customerHeader, ...editData];
      } else {
        config = [...defaultDataConfig, ...authData];
      }

      if (participantType === 'PROVIDER') {
        config.push({
          key: 'isCustomerSpecificURLs',
          label: 'Is Customer Specific URLs Required',
          defaultValue: 'no',
          type: 'radio',
          size: 12,
          values: [
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' }
          ]
        });
      }
      setDataConfig([...config]);
      setFormData({
        ...props.formData,
        isCustomerSpecificURLs:
          props.formData?.operationUrls && Object.keys(props.formData?.operationUrls).length > 0 ? 'yes' : 'no',
        ...formDataList,
        caption: props.formData?.authContext?.connectionNote
      });
    } else {
      setDataConfig([...requestRelationConfig]);
      setFormData({
        ...props.formData
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    additionalCreds,
    authenticationParticipantData,
    authenticationParticipantLoading,
    participantType,
    partnerCode,
    workflowSubscribersDataLoading,
    partnerIdentifiersData,
    partnerIdentifiersLoading,
    props.edit,
    props.formData,
    props.workflowCode,
    authenticationAggregatorData
  ]);

  if (partnerIdentifiersLoading || authenticationParticipantLoading) {
    return <Loading />;
  }

  const getAuthentication = (authenticationParticipantData: any) => {
    const serviceNameFormObj = _.filter(
      workflowSubscribersData?.subscribedParticipantWorkflow?.content || [],
      (o: any) => o.workflowCode === props.workflowCode
    );

    let auth = _.filter(
      authenticationParticipantData?.fetchAuthentication?.content || [],
      (o: any) => o?.authContext === serviceNameFormObj[0]?.authContext
    );

    if (auth?.length === 0) {
      auth = _.filter(authenticationParticipantData?.fetchAuthentication?.content || [], (o: any) => o.defaultAuth);
    }
    return auth?.[0] || {};
  };
  const mapParameterValue = (key: any, value: any) => {
    const list = key.split('/');
    if (list.length > 1) {
      if (additionalCreds === null || additionalCreds === undefined) {
        additionalCreds = {};
      }
      _.set(additionalCreds, list, value);
    }
  };

  const handleManageParticipantDialogSubmit = (formData: any) => {
    _.keys(formData || []).forEach(key => {
      mapParameterValue(key, key === 'secret' ? encodeURI(formData[key]) : formData[key]);
    });
    const queryOperationsList: any = {};
    _.keys(operations || []).forEach(key => {
      if (operations[key].url || operations[key].noOps === true) {
        queryOperationsList[key] = {
          url: operations[key].url ? operations[key].url : null,
          noOps: operations[key].noOps,
          operationName: operations[key].operationName
        };
      }
    });
    setQueryOperations({ ...queryOperationsList });
    props.handleDialogClose();
    addCustomerProvider({
      variables: {
        workflowCode: props.workflowCode,
        providerCode:
          props.formData.participantType !== 'AGGREGATOR' ? getProviderCode(props.formData.partnerCode) : 'DEFAULT',
        customerCode: getCustomerCode(formData.partnerCode),
        aggregatorCode: props.formData.participantType === 'AGGREGATOR' ? props.formData.partnerCode : 'DEFAULT',
        providerCustomerCode: formData.providerCustomerCode
          ? formData.providerCustomerCode
          : props.formData.customerCode,
        additionalCreds: additionalCreds,
        status:
          selectedKey === 'NO_RELATION'
            ? participantType === 'PROVIDER'
              ? 'ENABLED'
              : 'REQUESTED'
            : props.formData['status'],
        operationUrls:
          participantType === 'PROVIDER' && formData.isCustomerSpecificURLs === 'yes' ? queryOperationsList : {},
        method: props.edit[partnerCode] && selectedKey !== 'NO_RELATION' ? 'PUT' : 'POST'
      }
    })
      .then(() => {
        enqueueSnackbar('Relationship Added successfully !!', { variant: 'success', preventDuplicate: true });
        props.customerProviderRefresh();
        props.participantSubscriberRefresh();
        if (props.workflowCode === 'freightTracking') {
          props.aggregatorSubscriberRefresh();
        }
        if (!props.edit[partnerCode]) {
          getWorkflowSummary();
        }
      })
      .catch(() => {
        enqueueSnackbar('Unable to add relationship !!', { variant: 'error', preventDuplicate: true });
      });
  };

  const handleOperationDataChange = (e: any, key: any, field: string) => {
    const obj = operations[key];
    obj[field] = field !== 'noOps' ? e.target.value : e.target.checked;
    setOperations({ ...operations });
  };

  const getLatestValues = (dataObj: any) => {
    setFormData({
      ...formData,
      isCustomerSpecificURLs: dataObj.isCustomerSpecificURLs || 'no'
    });
  };

  return (
    <div>
      <DialogForm
        key={`ManageRelation-${partnerCode}`}
        openFn={props.dialogOpen}
        closeFn={props.handleDialogClose}
        dataConfig={[...dataConfig]}
        handleDialogClose={props.handleDialogClose}
        handleDialogSubmit={handleManageParticipantDialogSubmit}
        submitlabel="Submit"
        dialogId="new-participant-dialog"
        title="Manage Relation"
        caption={formData?.caption}
        getLatestValues={getLatestValues}
        data={{ ...formData }}
        handleStatusToggleChange={handleStatusToggleChange}
        existingDisable={props.existingDisable}
        supportUser={props.supportUser}
        disableSubmitButton={selectedKey === 'ENABLED' ? true : false}
      >
        {participantType === 'PROVIDER' && formData.isCustomerSpecificURLs === 'yes' && !props.supportUser ? (
          <Box m={1}>
            <Box className={classes.grid}>
              <Typography variant="subtitle1" style={{ padding: '8px' }}>
                Operations
              </Typography>
            </Box>
            <Box className={classes.grid} style={{ padding: '8px' }}>
              <Grid container>
                <Grid item xs={4}>
                  <Typography className={classes.headers}>Name</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className={classes.headers}>URL</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography className={classes.headers}>No Ops</Typography>
                </Grid>
              </Grid>
            </Box>
            {Object.keys(operations || {}).map((operationCode: any, index: number) => (
              <Grid container style={{ width: '100%' }} spacing={3} alignItems="flex-end">
                <Grid item xs={4} justify="center" alignItems="flex-end">
                  <Typography noWrap>{operations[operationCode].operationName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    data-id={`url-${operationCode}`}
                    id={`url-${operationCode}`}
                    className={classes.textField}
                    value={operations[operationCode].url || ''}
                    onChange={e => handleOperationDataChange(e, operationCode, 'url')}
                    disabled={operationCode ? operations[operationCode].noOps : false}
                  ></TextField>
                </Grid>
                <Grid item xs={2}>
                  <Checkbox
                    data-id={`noOps-${operationCode}`}
                    checked={operations[operationCode].noOps || false}
                    onChange={e => handleOperationDataChange(e, operationCode, 'noOps')}
                    name="NoOps"
                    color="primary"
                  />
                </Grid>
              </Grid>
            ))}
          </Box>
        ) : (
          ''
        )}
      </DialogForm>
    </div>
  );
};
export default ManageRelationDialog;
